import React, { Fragment, useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";
import SubmitButton from "../Forms/SubmitButton";
import * as Yup from "yup";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^\d{6}$/, "OTP must be 6 digits"),
});

export default function ModalBatchOTP(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const current_staff = useSelector((state) => state.auth.current_staff);
  const { total, count, hasStaging, refund_group, onSuccess } = props;
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpRecipients, setOtpRecipients] = useState("");

  useEffect(() => {
    sendOTP();
  }, []);

  const sendOTP = async () => {
    try {
      setSendingOtp(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}payments/batch/send-otp`,
        {
          staff_id: current_staff.id,
        },
      );

      if (response.data.success) {
        setOtpSent(true);
        setOtpRecipients(response.data.message || "OTP sent");
        toast.success(response.data.message || "OTP sent");
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        otp: values.otp,
        staff_id: current_staff.id,
        send_notification: values.send_notification || false,
        remove_tag: values.remove_tag || false,
      };
      
      // Add refund_group if provided
      if (refund_group) {
        payload.refund_group = refund_group;
      }
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}payments/batch/process`,
        payload,
      );

      if (response.data.success) {
        toast.success(response.data.message);
        if (onSuccess) onSuccess();
        dispatch(closeModal());
      } else {
        toast.error(response.data.message || "Failed to process batch");
      }
    } catch (error) {
      console.error("Error processing batch:", error);
      toast.error(error.response?.data?.message || "Failed to process batch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                    onClick={() => dispatch(closeModal())}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                <div className="pr-8 w-full">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h1"
                      className="text-lg font-medium text-gray-900 mb-4"
                    >
                      Confirm Batch Refund
                    </Dialog.Title>

                    {hasStaging && (
                      <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-yellow-800 mb-1">
                          🧪 STAGING MODE
                        </p>
                        <p className="text-xs text-yellow-700">
                          This batch contains staging bookings. OTP verification
                          is bypassed - enter any value to proceed.
                        </p>
                      </div>
                    )}

                    <div className="mb-6">
                      <p className="text-sm text-gray-700 mb-2">
                        Processing{" "}
                        <span className="font-medium">
                          R {total.toFixed(2)}
                        </span>{" "}
                        across <span className="font-medium">{count}</span>{" "}
                        refund{count !== 1 ? "s" : ""}
                      </p>
                      {sendingOtp ? (
                        <p className="text-sm text-gray-500">Sending OTP...</p>
                      ) : otpSent ? (
                        <div className="space-y-1">
                          <p className="text-sm text-green-600 font-medium">
                            ✓ {otpRecipients}
                          </p>
                          {hasStaging && (
                            <p className="text-xs text-yellow-600">
                              (Staging mode - any value accepted)
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-red-600">
                          Failed to send OTP
                        </p>
                      )}
                    </div>

                    <Formik
                      initialValues={{
                        otp: hasStaging ? "000000" : "",
                        send_notification: false,
                        remove_tag: false,
                      }}
                      validationSchema={validationSchema}
                      onSubmit={handleSubmit}
                    >
                      {({ errors, touched, isSubmitting }) => (
                        <Form>
                          <div className="space-y-4">
                            {/* Send Notification Checkbox */}
                            <div className="flex items-center">
                              <Field
                                type="checkbox"
                                name="send_notification"
                                id="send_notification"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="send_notification"
                                className="ml-2 block text-sm text-gray-700"
                              >
                                Send WhatsApp notification to recipients
                              </label>
                            </div>

                            {/* Remove Tag Checkbox */}
                            <div className="flex items-center">
                              <Field
                                type="checkbox"
                                name="remove_tag"
                                id="remove_tag"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <label
                                htmlFor="remove_tag"
                                className="ml-2 block text-sm text-gray-700"
                              >
                                Remove tag after successful refund
                              </label>
                            </div>

                            {/* OTP */}
                            <div>
                              <label
                                htmlFor="otp"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Enter OTP:
                              </label>
                              <Field
                                type="text"
                                name="otp"
                                maxLength="6"
                                className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-center text-2xl tracking-widest"
                                placeholder="000000"
                                autoFocus
                              />
                              {errors.otp && touched.otp && (
                                <div className="text-red-600 text-sm mt-1">
                                  {errors.otp}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Buttons */}
                          <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                            <SubmitButton
                              text="Confirm"
                              isSubmitting={isSubmitting}
                              disabled={!otpSent}
                              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:col-start-2 sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                            />
                            <button
                              type="button"
                              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                              onClick={() => dispatch(closeModal())}
                            >
                              Cancel
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
